import { useEffect, useState } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { useNavigate, useParams, Link } from "react-router-dom";
import { db, auth } from "../../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { GraduationCap, LogOut, User, BookOpen, Mail, BarChart3, Lock, Zap, Award, CheckCircle, QrCode, ArrowLeft, Check, Package, Tag, Building2, Copy } from "lucide-react";
import AuthModal from "../../components/AuthModal";
import { createPendingCoursePayment } from "../../services/paymentService";

export default function CourseCheckoutNew() {
  const { user } = useAuthContext();
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [course, setCourse] = useState<any>(null);
  const [instructor, setInstructor] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountError, setDiscountError] = useState("");
  const [applyingDiscount, setApplyingDiscount] = useState(false);
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  
  const themeColor = instructor?.themeColor || instructor?.buttonColor || "#8B5CF6";
  const headingColor = instructor?.headingColor || "#1F2937";
  const buttonColor = instructor?.buttonColor || instructor?.themeColor || "#8B5CF6";

  useEffect(() => {
    async function load() {
      if (!courseId) return;

      const snap = await getDoc(doc(db, "courses", courseId));
      if (snap.exists()) {
        const courseData = snap.data();
        setCourse(courseData);
        
        if (courseData.instructorId) {
          const instructorSnap = await getDoc(doc(db, "users", courseData.instructorId));
          if (instructorSnap.exists()) {
            setInstructor(instructorSnap.data());
          }
        }
      }

      setLoading(false);
    }
    load();
  }, [courseId]);

  const applyDiscount = async () => {
    if (!discountCode.trim()) return;
    
    setApplyingDiscount(true);
    setDiscountError("");
    
    // Simulate discount validation
    setTimeout(() => {
      if (discountCode === "WELCOME10") {
        setDiscount(10);
      } else if (discountCode === "SAVE20") {
        setDiscount(20);
      } else {
        setDiscountError("รหัสส่วนลดไม่ถูกต้อง");
      }
      setApplyingDiscount(false);
    }, 500);
  };

  const calculateFinalPrice = () => {
    if (!course?.price) return 0;
    return Math.round(course.price * (1 - discount / 100));
  };

  const handlePayment = async () => {
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    if (!paymentProof) {
      alert("กรุณาอัปโหลดสลิปการโอนเงิน");
      return;
    }

    setProcessing(true);
    try {
      const userName = user.displayName || user.email || "ผู้ใช้";
      
      await createPendingCoursePayment(
        user.uid,
        userName,
        courseId!,
        course.title,
        instructor.uid,
        calculateFinalPrice(),
        "bank_transfer",
        previewUrl
      );
      
      alert("แจ้งการโอนเงินสำเร็จ! รอผู้สอนตรวจสอบและอนุมัติคุณเข้าเรียน");
      navigate(instructor.username ? `/${instructor.username}?section=my` : `/instructor/${instructor.uid}?section=my`);
    } catch (error) {
      console.error("Payment error:", error);
      alert("เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-800">ไม่พบคอร์สที่ต้องการ</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      {/* Navigation Menu */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to={instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              {instructor?.profileImage ? (
                <img src={instructor.profileImage} alt={instructor.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-white shadow-lg" />
              ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg" style={{ background: themeColor }}>
                  {instructor?.name?.[0]?.toUpperCase() || "T"}
                </div>
              )}
              <div>
                <h2 className="font-bold text-gray-800">{instructor?.name || "ผู้สอน"}</h2>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  ผู้สอนมืออาชีพ
                </p>
              </div>
            </Link>
            
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <Link to={instructor?.username ? `/${instructor.username}` : `/instructor/${instructor?.uid}`}>
                    <button className="px-4 py-2 rounded-xl font-semibold transition-all flex items-center gap-2 text-gray-600 hover:text-gray-800">
                      <BookOpen className="w-4 h-4" />
                      คอร์สทั้งหมด
                    </button>
                  </Link>
                  <button
                    onClick={() => auth.signOut()}
                    className="px-4 py-2 bg-red-50 hover:bg-red-100 rounded-xl font-semibold text-red-600 transition-all flex items-center gap-2"
                  >
                    <LogOut className="w-4 h-4" />
                    ออกจากระบบ
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className="px-6 py-2 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 flex items-center gap-2"
                  style={{ background: themeColor }}
                >
                  <User className="w-4 h-4" />
                  เข้าสู่ระบบ
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 max-w-6xl py-12 pt-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">กลับ</span>
          </button>
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">ชำระเงินคอร์สเรียน</h1>
            <p className="text-gray-600">ยืนยันการชำระเงินเพื่อเริ่มเรียนได้ทันที</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Course Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Course Info Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border-2 border-blue-500">
              <div className="flex items-start gap-4">
                {course.thumbnail && (
                  <img
                    src={course.thumbnail}
                    alt={course.title}
                    className="w-32 h-32 object-cover rounded-xl flex-shrink-0"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="w-5 h-5 text-blue-600" />
                    <h3 className="text-xl font-bold text-gray-900">{course.title}</h3>
                  </div>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{course.description}</p>
                  
                  {instructor && (
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <User className="w-4 h-4" />
                      <span>โดย {instructor.name}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>เข้าถึงคอร์สได้ตลอดชีพ</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>เรียนได้ทุกที่ทุกเวลา</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>รับใบประกาศนียบัตร</span>
                </div>
              </div>
            </div>

            {/* Discount Code */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <Tag className="w-5 h-5 text-gray-700" />
                <h3 className="text-xl font-bold text-gray-900">รหัสส่วนลด</h3>
              </div>
              
              <div className="flex gap-3">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase());
                    setDiscountError("");
                    setDiscount(0);
                  }}
                  placeholder="ใส่รหัสส่วนลด"
                  className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 transition-colors"
                />
                <button
                  onClick={applyDiscount}
                  disabled={applyingDiscount || !discountCode.trim()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {applyingDiscount ? "กำลังตรวจสอบ..." : "ใช้รหัส"}
                </button>
              </div>
              
              {discountError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2">
                  <span className="text-red-700 text-sm">{discountError}</span>
                </div>
              )}
              
              {discount > 0 && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 font-semibold">ใช้รหัสส่วนลดสำเร็จ! ลด {discount}%</span>
                </div>
              )}
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">ข้อมูลการชำระเงิน</h3>
              
              {/* QR Code */}
              {instructor?.paymentQRCode && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <QrCode className="w-5 h-5 text-green-600" />
                    <h4 className="font-semibold text-gray-900">สแกน QR Code PromptPay</h4>
                  </div>
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6">
                    <div className="text-center">
                      <img
                        src={instructor.paymentQRCode}
                        alt="Payment QR Code"
                        className="w-48 h-48 mx-auto mb-3 border-4 border-white shadow-lg rounded-xl"
                      />
                      <p className="text-sm text-gray-600">
                        สแกน QR Code ด้วยแอปธนาคารของคุณ
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Account */}
              {instructor?.bankAccount && (
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <h4 className="font-semibold text-gray-900">เลขบัญชีธนาคาร</h4>
                  </div>
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        {instructor.bankName && (
                          <p className="text-xs text-gray-600 mb-1">{instructor.bankName}</p>
                        )}
                        <p className="text-lg font-bold text-gray-900 mb-1">{instructor.bankAccount}</p>
                        {instructor.accountName && (
                          <p className="text-sm font-semibold text-gray-700">ชื่อบัญชี: {instructor.accountName}</p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(instructor.bankAccount);
                          alert('คัดลอกเลขบัญชีแล้ว!');
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-all flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        คัดลอก
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Upload Payment Proof */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  อัปโหลดสลิปการโอนเงิน *
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setPaymentProof(file);
                      setPreviewUrl(URL.createObjectURL(file));
                    }
                  }}
                  className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer"
                />
                {previewUrl && (
                  <div className="mt-4">
                    <img
                      src={previewUrl}
                      alt="Payment Proof Preview"
                      className="w-full h-48 object-contain rounded-xl border-2 border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Trust Badges */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="flex items-center justify-center mb-2">
                    <Lock className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="text-xs font-semibold text-gray-800">ชำระเงินปลอดภัย</div>
                </div>
                <div>
                  <div className="flex items-center justify-center mb-2">
                    <Zap className="w-8 h-8 text-yellow-500" />
                  </div>
                  <div className="text-xs font-semibold text-gray-800">เข้าเรียนทันที</div>
                </div>
                <div>
                  <div className="flex items-center justify-center mb-2">
                    <Award className="w-8 h-8 text-blue-600" />
                  </div>
                  <div className="text-xs font-semibold text-gray-800">รับประกันคุณภาพ</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Order Summary (Sticky) */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold text-gray-900 mb-4">สรุปยอดชำระ</h3>
              
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">ราคาคอร์ส</span>
                  <span className="font-semibold text-gray-900">฿{course.price?.toLocaleString()}</span>
                </div>
                
                {discount > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">ส่วนลด ({discount}%)</span>
                    <span className="font-semibold text-green-600">-฿{Math.round(course.price * discount / 100).toLocaleString()}</span>
                  </div>
                )}
                
                <div className="border-t pt-3 flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">ยอดรวมทั้งสิ้น</span>
                  <span className="text-3xl font-black text-blue-600">
                    ฿{calculateFinalPrice().toLocaleString()}
                  </span>
                </div>
              </div>

              <button
                onClick={handlePayment}
                disabled={processing || !paymentProof}
                className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {processing ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    กำลังดำเนินการ...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    ยืนยันการชำระเงิน
                  </>
                )}
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
                <Lock className="w-4 h-4 text-green-500" />
                <span>การชำระเงินปลอดภัย เข้ารหัสด้วย SSL</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        themeColor={themeColor}
      />
    </div>
  );
}
